// src/pages/admin/promotions/AllPromotions.jsx
import { useState, useEffect } from 'react'
import { Card, CardContent } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import InlineLoader from '../../../components/ui/InlineLoader'
import ButtonSpinner from '../../../components/ui/ButtonSpinner'
import AuthService from '../../../services/authService'
import { usePermissions } from '../../../hooks/usePermissions'
import { 
  Plus, 
  Search, 
  Filter, 
  RefreshCw, 
  Eye, 
  Edit, 
  Trash2,
  Tag,
  CheckCircle,
  XCircle,
  TrendingUp,
  Clock,
  Users,
  AlertCircle
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import PromotionService from '../../../services/promotionService'
import CustomerGroupService from '../../../services/customerGroupService'
import ProductService from '../../../services/productService'
import { PromotionModel, PROMOTION_TYPE_CONFIG, PRIORITY_CONFIG } from '../../../utils/promotionModel'
import ViewPromotionModal from '../../../components/modals/ViewPromotionModal'
import DeletePromotionModal from '../../../components/modals/DeletePromotionModal'

// Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center space-x-2 ${
    type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
    <span>{message || 'Action completed'}</span>
    <button onClick={onClose} className="ml-2">
      <XCircle className="h-4 w-4" />
    </button>
  </div>
)

const AllPromotions = () => {
  const navigate = useNavigate()

  // User permissions and RBAC
  const { hasPermission } = usePermissions()
  const currentUser = AuthService.getCurrentUser()
  const userBranchId = currentUser?.branchId
  const isManager = currentUser?.role?.toLowerCase() === 'manager'
  const isAdmin = currentUser?.role?.toLowerCase() === 'admin'
  const isSuperAdmin = currentUser?.role?.toLowerCase() === 'super_admin' || currentUser?.role?.toLowerCase() === 'superadmin'
  
  console.log('🎯 Promotions RBAC Check:', {
    userId: currentUser?._id,
    role: currentUser?.role,
    branchId: userBranchId,
    branchName: currentUser?.branch?.name || 'Unknown',
    isManager,
    isAdmin,
    isSuperAdmin,
    permissions: {
      canViewPromotions: hasPermission('promotion.view') || isManager || isAdmin || isSuperAdmin,
      canCreatePromotions: hasPermission('promotion.create') || isAdmin || isSuperAdmin,
      canEditPromotions: hasPermission('promotion.edit') || isAdmin || isSuperAdmin,
      canDeletePromotions: hasPermission('promotion.delete') || isAdmin || isSuperAdmin
    }
  })

  // State management
  const [promotions, setPromotions] = useState([])
  const [filteredPromotions, setFilteredPromotions] = useState([])
  const [customerGroups, setCustomerGroups] = useState([])
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState({})
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [priorityFilter, setPriorityFilter] = useState('')
  const [selectedPromotion, setSelectedPromotion] = useState(null)
  const [toast, setToast] = useState(null)

  // Modal states
  const [showViewModal, setShowViewModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  // Initialize component
  useEffect(() => {
    fetchPromotions()
    fetchDropdownData()
  }, [])

  // Filter promotions whenever dependencies change
  useEffect(() => {
    filterPromotions()
  }, [promotions, searchTerm, typeFilter, statusFilter, priorityFilter])

  // Helper function to safely access nested properties
  const safeGet = (obj, path, defaultValue = null) => {
    try {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : defaultValue
      }, obj)
    } catch {
      return defaultValue
    }
  }

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  // Fetch promotions from API
  const fetchPromotions = async () => {
    try {
      setLoading(true)
      console.log('🎉 Fetching promotions...')

      // For now, promotions are typically global and not branch-specific
      // But we can add branch filtering in the future if needed
      const apiParams = { page: 1, limit: 100 }
      
      console.log('🎯 Promotions API call:', {
        userRole: currentUser?.role,
        isManager,
        isAdmin,
        params: apiParams
      })

      const response = await PromotionService.getAllPromotions(apiParams)

      if (response && response.success) {
        const promotionsData = safeGet(response, 'data.promotions', []) || 
                             safeGet(response, 'data', [])

        console.log('✅ Promotions fetched successfully:', promotionsData.length, 'promotions')

        // Format promotions for display
        const formattedPromotions = promotionsData.map(promotion => 
          PromotionModel.formatForDisplay(promotion)
        )

        setPromotions(formattedPromotions)
        showToast('Promotions loaded successfully')
      } else {
        throw new Error(response?.message || 'Failed to fetch promotions')
      }
    } catch (error) {
      console.error('❌ Error fetching promotions:', error)
      showToast('Failed to load promotions', 'error')
    } finally {
      setLoading(false)
    }
  }

  // Fetch dropdown data
  const fetchDropdownData = async () => {
    try {
      console.log('📦 Fetching dropdown data...')

      const [customerGroupsRes, productsRes] = await Promise.all([
        CustomerGroupService.getAllCustomerGroups().catch(err => {
          console.warn('Customer groups fetch failed:', err)
          return { success: false, data: [] }
        }),
        ProductService.getAllProducts().catch(err => {
          console.warn('Products fetch failed:', err)
          return { success: false, data: [] }
        })
      ])

      // Set customer groups
      if (customerGroupsRes?.success) {
        const groups = customerGroupsRes.data?.customerGroups || customerGroupsRes.data || []
        setCustomerGroups(groups)
        console.log('✅ Customer groups loaded:', groups.length)
      }

      // Set products
      if (productsRes?.success) {
        const productsList = productsRes.data?.products || productsRes.data || []
        setProducts(productsList)
        console.log('✅ Products loaded:', productsList.length)
      }
    } catch (error) {
      console.error('❌ Error fetching dropdown data:', error)
    }
  }

  // Filter and search logic
  const filterPromotions = () => {
    let filtered = [...promotions]

    // Search filter
    if (searchTerm) {
      filtered = PromotionModel.searchPromotions(filtered, searchTerm)
    }

    // Type filter
    if (typeFilter) {
      filtered = PromotionModel.filterByType(filtered, typeFilter)
    }

    // Status filter
    if (statusFilter !== '') {
      const isActive = statusFilter === 'active'
      filtered = PromotionModel.filterByStatus(filtered, isActive)
    }

    // Priority filter
    if (priorityFilter) {
      filtered = filtered.filter(promotion => promotion.priority === parseInt(priorityFilter))
    }

    setFilteredPromotions(PromotionModel.sortPromotions(filtered, 'createdAt', 'desc'))
  }

  // Handle input changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value)
  }

  const handleTypeFilterChange = (e) => {
    setTypeFilter(e.target.value)
  }

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value)
  }

  const handlePriorityFilterChange = (e) => {
    setPriorityFilter(e.target.value)
  }

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('')
    setTypeFilter('')
    setStatusFilter('')
    setPriorityFilter('')
  }

  // Handle action buttons
  const handleViewPromotion = (promotion) => {
    setSelectedPromotion(promotion)
    setShowViewModal(true)
  }

  const handleEditPromotion = (promotion) => {
    navigate(`/admin/promotions/edit/${promotion._id}`)
  }

  const handleDeletePromotion = (promotion) => {
    setSelectedPromotion(promotion)
    setShowDeleteModal(true)
  }

  const handleToggleStatus = async (promotion) => {
    const promotionId = promotion._id
    setActionLoading(prev => ({ ...prev, [promotionId]: true }))

    try {
      const newStatus = !promotion.isActive
      const response = await PromotionService.togglePromotionStatus(promotionId, newStatus)

      if (response && response.success) {
        // Update local state
        setPromotions(prev => prev.map(p => 
          p._id === promotionId 
            ? { ...p, isActive: newStatus }
            : p
        ))
        showToast(`Promotion ${newStatus ? 'activated' : 'deactivated'} successfully`)
      } else {
        showToast('Failed to update promotion status', 'error')
      }
    } catch (error) {
      console.error('Error toggling promotion status:', error)
      showToast('Failed to update promotion status', 'error')
    } finally {
      setActionLoading(prev => ({ ...prev, [promotionId]: false }))
    }
  }

  // Handle successful operations
  const handlePromotionDeleted = () => {
    setShowDeleteModal(false)
    setSelectedPromotion(null)
    fetchPromotions() // Refresh list
    showToast('Promotion deleted successfully')
  }

  // Get statistics
  const getStatistics = () => {
    return PromotionModel.calculateStats(promotions)
  }

  const statistics = getStatistics()

  // Loading state
  if (loading) {
    return (
      <InlineLoader text="Loading promotions..." />
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Toast Notification */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Promotions</h1>
            {/* {isManager && (
              <p className="text-sm text-gray-600 mt-1">
                📖 Manager View - You can view all promotions but cannot create, edit, or delete them
              </p>
            )} */}
            {/* {isAdmin && (
              <p className="text-sm text-gray-600 mt-1">
                🔧 Administrator - Full access to all promotion features
              </p>
            )} */}
          </div>
          <div className="flex space-x-3">
            <Button 
              onClick={fetchPromotions}
              leftIcon={RefreshCw}
              variant="outline"
              disabled={loading}
              className="bg-white border-gray-300"
            >
              Refresh
            </Button>
            {/* Only admins and super admins can create promotions */}
            {(isAdmin || isSuperAdmin) && (
              <Button 
                onClick={() => navigate('/admin/promotions/create')}
                leftIcon={Plus}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                Create Promotion
              </Button>
            )}
            {/* Show restricted message for managers (but not for admins/super admins) */}
            {/* {isManager && !isAdmin && !isSuperAdmin && (
              <div className="text-sm text-gray-500 bg-gray-100 px-3 py-2 rounded-md">
                📖 Viewing Mode - Contact Admin to Create Promotions
              </div>
            )} */}
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {/* Total Promotions */}
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Tag className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Promotions</p>
                  <p className="text-2xl font-bold text-gray-900">{statistics.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Active Promotions */}
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Active</p>
                  <p className="text-2xl font-bold text-gray-900">{statistics.active}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Currently Running */}
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Clock className="h-8 w-8 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Currently Running</p>
                  <p className="text-2xl font-bold text-gray-900">{statistics.currentlyRunning}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Inactive Promotions */}
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-gray-100 rounded-lg">
                  <XCircle className="h-8 w-8 text-gray-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Inactive</p>
                  <p className="text-2xl font-bold text-gray-900">{statistics.inactive}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-6">
            {/* Filter Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Filter className="h-5 w-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Filter Promotions</h2>
              </div>
              {(searchTerm || typeFilter || statusFilter || priorityFilter) && (
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="text-orange-500 border-orange-500 hover:bg-orange-50"
                >
                  Clear Filters
                </Button>
              )}
            </div>

            {/* Filter Controls */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <Input
                  type="text"
                  placeholder="Search promotions by name..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="pl-10 h-11 border-gray-300"
                />
              </div>

              {/* Type Filter */}
              <div>
                <select
                  value={typeFilter}
                  onChange={handleTypeFilterChange}
                  className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.5em 1.5em'
                  }}
                >
                  <option value="">All Types</option>
                  {Object.values(PROMOTION_TYPE_CONFIG).map(type => (
                    <option key={type.value} value={type.value}>
                      {type.icon} {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.5em 1.5em'
                  }}
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {/* Priority Filter */}
              <div>
                <select
                  value={priorityFilter}
                  onChange={handlePriorityFilterChange}
                  className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.5em 1.5em'
                  }}
                >
                  <option value="">All Priorities</option>
                  {Object.values(PRIORITY_CONFIG).map(priority => (
                    <option key={priority.value} value={priority.value}>
                      {priority.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Results info */}
            <div className="mt-4 text-sm text-gray-600">
              Showing {filteredPromotions.length} of {promotions.length} promotions
            </div>
          </CardContent>
        </Card>

        {/* Promotions List */}
        <Card>
          <CardContent className="p-6">
            {filteredPromotions.length === 0 ? (
              <div className="text-center py-12">
                <Tag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {promotions.length === 0 ? 'No promotions found' : 'No promotions match your filters'}
                </h3>
                <p className="text-gray-500 mb-4">
                  {promotions.length === 0 
                    ? "You haven't created any promotions yet." 
                    : "Try adjusting your search criteria."
                  }
                </p>
                {promotions.length === 0 && (isAdmin || isSuperAdmin) && (
                  <Button
                    onClick={() => navigate('/admin/promotions/create')}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Promotion
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border-collapse">
                  {/* Table Header */}
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Promotion Name
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Discount
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Target Audience
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Period
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  {/* Table Body */}
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredPromotions.map((promotion) => (
                      <tr key={promotion._id} className="hover:bg-gray-50">
                        {/* Promotion Name */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900 mb-1">
                              {promotion.formattedName}
                            </div>
                            {promotion.description && (
                              <div className="text-sm text-gray-500 truncate max-w-xs">
                                {promotion.description}
                              </div>
                            )}
                            <div className="flex items-center mt-2 space-x-2">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${promotion.priorityBadgeClasses}`}>
                                {promotion.formattedPriority}
                              </span>
                              {promotion.productsCount > 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                  {promotion.productsCount} Products
                                </span>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Type */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${promotion.typeBadgeClasses}`}>
                            {PromotionModel.getTypeIcon(promotion.promotionType)} {promotion.formattedType}
                          </span>
                        </td>

                        {/* Discount */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">
                            {promotion.formattedDiscount}
                          </div>
                        </td>

                        {/* Target Audience */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Users className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900">
                              {promotion.targetAudienceName}
                            </span>
                          </div>
                        </td>

                        {/* Period */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm text-gray-900">
                              {promotion.formattedStartDate}
                            </div>
                            <div className="text-sm text-gray-500">
                              to {promotion.formattedEndDate}
                            </div>
                            {promotion.isCurrentlyActive && (
                              <div className="text-xs text-green-600 font-medium mt-1">
                                {promotion.daysRemaining > 0 
                                  ? `${promotion.daysRemaining} days left`
                                  : 'Expires today'
                                }
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col space-y-1">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${promotion.statusBadgeClasses}`}>
                              {promotion.isActive ? 'Active' : 'Inactive'}
                            </span>
                            {promotion.isCurrentlyActive && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <div className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1 animate-pulse"></div>
                                Running
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="px-2  whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center ">
                            <button
                              onClick={() => handleViewPromotion(promotion)}
                              className="p-2 text-gray-400 hover:text-gray-600"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            {/* Only admins can edit promotions */}
                            {isAdmin && (
                              <button
                                onClick={() => handleEditPromotion(promotion)}
                                className="p-2 text-gray-400 hover:text-gray-600"
                                title="Edit"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            )}
                            {/* Only admins can delete promotions */}
                            {isAdmin && (
                              <button
                                onClick={() => handleDeletePromotion(promotion)}
                                className="p-2 text-gray-400 hover:text-gray-600"
                                title="Delete"
                                disabled={actionLoading[promotion._id]}
                              >
                                {actionLoading[promotion._id] ? (
                                  <ButtonSpinner />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </button>
                            )}
                            {/* Show restricted message for managers */}
                            {/* {isManager && (
                              <span className="text-xs text-gray-500 px-2">
                                View Only
                              </span>
                            )} */}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      <ViewPromotionModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        promotion={selectedPromotion}
      />

      <DeletePromotionModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        promotion={selectedPromotion}
        onPromotionDeleted={handlePromotionDeleted}
      />
    </div>
  )
}

export default AllPromotions